import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CalendarClock, CheckCircle2, Clock, Download,
  FileWarning, Flame, GraduationCap, Heart, ListChecks, ShieldCheck, Users,
} from "lucide-react";
import { toast } from "sonner";
import { apiRequest, downloadFile } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  RisksByCategoryChart, RisksByLevelChart, DeadlinesByMonthChart, ActionsStatusChart,
} from "@/components/DashboardCharts";

const SEVERITY_COLORS = {
  overdue: "bg-red-100 text-red-800 border-red-200",
  urgent: "bg-orange-100 text-orange-800 border-orange-200",
  soon: "bg-amber-100 text-amber-800 border-amber-200",
  scheduled: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const LEVEL_COLORS = {
  alto: "bg-red-100 text-red-800",
  significativo: "bg-orange-100 text-orange-800",
  moderato: "bg-amber-100 text-amber-800",
  basso: "bg-emerald-100 text-emerald-800",
};

const DVR_STATUS_LABELS = {
  non_avviato: "Non avviato",
  in_progress: "In corso",
  draft: "Bozza",
  signed: "Firmato",
  expired: "Scaduto",
  revised: "Revisione",
};

const DOC_CATEGORY_LABELS = {
  nomine: "Nomine",
  attestati_formazione: "Attestati formazione",
  planimetrie: "Planimetrie",
  certificazioni: "Certificazioni",
  schede_sicurezza: "SDS chimici",
  manutenzioni: "Manutenzioni",
  verbali: "Verbali",
  dpi: "Registro DPI",
};

function KpiCard({ icon: Icon, label, value, tone, testId, sublabel }) {
  return (
    <Card className="rounded-lg border-slate-200 shadow-none" data-testid={testId}>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs font-medium text-slate-500" data-testid={`${testId}-label`}>{label}</p>
          <p className="mt-1 font-heading text-2xl font-bold text-slate-950" data-testid={`${testId}-value`}>{value}</p>
          {sublabel && <p className="mt-0.5 text-xs text-slate-500">{sublabel}</p>}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${tone}`}><Icon className="h-5 w-5" /></div>
      </CardContent>
    </Card>
  );
}

export default function CompanyDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest(`/companies/${id}/dashboard`)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="company-dashboard-loading">Caricamento dashboard azienda...</div>;
  if (error) return <Alert variant="destructive" data-testid="company-dashboard-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>;
  if (!data) return null;

  const { company, dvr_status, completion_percentage, missing_fields, top_risks, actions_status,
    actions_total, deadlines, missing_categories, missing_documents_count, training, charts } = data;

  const completionTone = completion_percentage >= 90 ? "emerald" : completion_percentage >= 60 ? "amber" : "rose";
  const completionToneClass = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  }[completionTone];

  return (
    <div className="space-y-6" data-testid="company-dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="company-dashboard-back-button">
          <ArrowLeft className="h-4 w-4" /> Scheda azienda
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                await downloadFile(`/companies/${id}/dashboard/snapshot-pdf`, `executive_snapshot_${id}.pdf`);
                toast.success("Snapshot PDF scaricato");
              } catch (err) {
                toast.error("Snapshot fallito", { description: err.message });
              }
            }}
            data-testid="company-dashboard-snapshot-pdf-button"
          >
            <Download className="h-4 w-4" /> Snapshot PDF
          </Button>
          <Badge className="bg-blue-50 text-blue-900" data-testid="company-dashboard-company-badge">{company.business_name}</Badge>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="company-dashboard-hero">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Dashboard azienda</p>
            <h2 className="mt-1 font-heading text-3xl font-bold text-slate-950" data-testid="company-dashboard-title">{company.business_name}</h2>
            <p className="mt-1 text-sm text-slate-500">ATECO {company.ateco_code || "—"} {company.vat_number ? `· P.IVA ${company.vat_number}` : ""}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge className={completionToneClass} data-testid="company-dashboard-completion-badge">
              Completamento DVR: {completion_percentage}%
            </Badge>
            <Badge className="bg-slate-100 text-slate-700" data-testid="company-dashboard-status-badge">
              Stato DVR: {DVR_STATUS_LABELS[dvr_status] || dvr_status}
            </Badge>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={completion_percentage} className="h-2.5" data-testid="company-dashboard-progress" />
        </div>
        {missing_fields && missing_fields.length > 0 && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="company-dashboard-missing-fields">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Campi obbligatori mancanti</p>
            <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-amber-900">
              {missing_fields.map((f, i) => <li key={i} data-testid={`missing-field-${i}`}>{f}</li>)}
            </ul>
          </div>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="company-dashboard-kpi-grid">
        <KpiCard icon={Flame} label="Rischi alti aperti" value={top_risks.filter((r) => r.risk_level === "alto").length} tone="bg-rose-50 text-rose-700" testId="kpi-risks-alto" />
        <KpiCard icon={ListChecks} label="Azioni miglioramento" value={actions_total} sublabel={`${actions_status.scaduta} scadute · ${actions_status.in_corso} in corso`} tone="bg-blue-50 text-blue-900" testId="kpi-actions" />
        <KpiCard icon={CalendarClock} label="Scadenze 30gg" value={deadlines.overdue + deadlines.urgent + deadlines.soon} sublabel={`${deadlines.overdue} scadute · ${deadlines.urgent} urgenti`} tone="bg-amber-50 text-amber-700" testId="kpi-deadlines" />
        <KpiCard icon={FileWarning} label="Documenti mancanti" value={missing_documents_count} sublabel={`${missing_categories.length}/8 categorie`} tone="bg-orange-50 text-orange-700" testId="kpi-missing-docs" />
        <KpiCard icon={GraduationCap} label="Formazione scaduta" value={training.expired} sublabel={`${training.expiring} in scadenza`} tone="bg-red-50 text-red-700" testId="kpi-training-expired" />
        <KpiCard icon={Users} label="Lavoratori formati" value={`${training.workers_trained}/${training.workers_total || 0}`} tone="bg-emerald-50 text-emerald-700" testId="kpi-workers-trained" />
        <KpiCard icon={CheckCircle2} label="Azioni completate" value={actions_status.completata} tone="bg-emerald-50 text-emerald-700" testId="kpi-actions-completata" />
        <KpiCard icon={Clock} label="Azioni aperte" value={actions_status.aperta + actions_status.in_corso} tone="bg-slate-100 text-slate-700" testId="kpi-actions-aperte" />
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]" data-testid="company-dashboard-detail-grid">
        <Card className="rounded-lg border-slate-200 shadow-none" data-testid="company-dashboard-top-risks-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Rischi principali</CardTitle>
              <Button asChild variant="ghost" size="sm" data-testid="company-dashboard-risks-link"><Link to={`/aziende/${id}/rischi`}>Vedi tutti <ArrowRight className="h-3 w-3" /></Link></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {top_risks.length === 0 && <p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="company-dashboard-no-risks">Nessun rischio significativo aperto.</p>}
            {top_risks.map((r) => (
              <div key={r.id} className="rounded-md border border-slate-200 p-3" data-testid={`top-risk-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-slate-950 text-sm">{r.risk_name}</p>
                  <Badge className={LEVEL_COLORS[r.risk_level] || "bg-slate-50 text-slate-700"} data-testid={`top-risk-level-${r.id}`}>{r.risk_level}</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">{r.risk_category} · P {r.probability} × D {r.damage}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-none" data-testid="company-dashboard-deadlines-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Prossime scadenze</CardTitle>
              <Button asChild variant="ghost" size="sm" data-testid="company-dashboard-deadlines-link"><Link to="/scadenze">Tutte <ArrowRight className="h-3 w-3" /></Link></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {deadlines.next.length === 0 && <p className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="company-dashboard-no-deadlines">Nessuna scadenza in arrivo.</p>}
            {deadlines.next.map((it) => (
              <div key={it.id} className="rounded-md border border-slate-200 p-3" data-testid={`deadline-${it.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-slate-950 text-sm">{it.title}</p>
                  <Badge className={`border ${SEVERITY_COLORS[it.severity] || "bg-slate-50 text-slate-700"}`}>{it.severity}</Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">{it.source} · {it.due_date} · {it.days_remaining >= 0 ? `tra ${it.days_remaining}gg` : `scaduta da ${Math.abs(it.days_remaining)}gg`}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {missing_categories.length > 0 && (
        <Card className="rounded-lg border-amber-200 bg-amber-50/30 shadow-none" data-testid="company-dashboard-missing-docs-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Documenti da caricare</CardTitle>
              <Button asChild variant="ghost" size="sm" data-testid="company-dashboard-docs-link"><Link to={`/aziende/${id}/documenti`}>Vai a Documenti <ArrowRight className="h-3 w-3" /></Link></Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2" data-testid="company-dashboard-missing-cats-list">
              {missing_categories.map((c) => (
                <Badge key={c} className="bg-amber-100 text-amber-800" data-testid={`missing-cat-${c}`}>
                  {DOC_CATEGORY_LABELS[c] || c}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="company-dashboard-charts-section">
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Analisi grafica</p>
          <h3 className="mt-1 font-heading text-2xl font-bold text-slate-950">Distribuzione rischi, scadenze, azioni</h3>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <div data-testid="company-chart-card-risks-by-category">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Rischi per categoria</p>
            <RisksByCategoryChart data={charts?.risks_by_category || []} testId="company-chart-risks-by-category" />
          </div>
          <div data-testid="company-chart-card-risks-by-level">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Rischi per livello</p>
            <RisksByLevelChart data={charts?.risks_by_level || []} testId="company-chart-risks-by-level" />
          </div>
          <div data-testid="company-chart-card-deadlines-by-month">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Scadenze per mese</p>
            <DeadlinesByMonthChart data={charts?.deadlines_by_month || []} testId="company-chart-deadlines-by-month" />
          </div>
          <div data-testid="company-chart-card-actions-status">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Stato azioni miglioramento</p>
            <ActionsStatusChart data={charts?.actions_status || []} testId="company-chart-actions-status" />
          </div>
        </div>
      </section>
    </div>
  );
}
